class CanvasHeatmapOverlay {
  constructor(options = {}) {
    this.radius = options.radius || 30;
    this.gradient = options.gradient || [];
    this.data = [];
    this.canvas = null;
    this.colorRamp = null;

    // eslint-disable-next-line no-undef
    this.overlay = new google.maps.OverlayView();
    this.overlay.onAdd = () => this.onAdd();
    this.overlay.draw = () => this.draw();
    this.overlay.onRemove = () => this.onRemove();
  }

  set(key, value) {
    if (key === "gradient") {
      this.gradient = value;
      this.colorRamp = null;
      this.draw();
    }
  }

  setData(data) {
    this.data = data || [];
    this.draw();
  }

  setMap(map) {
    this.overlay.setMap(map);
  }

  getMap() {
    return this.overlay.getMap();
  }

  onAdd() {
    this.canvas = document.createElement("canvas");
    this.canvas.style.position = "absolute";
    this.canvas.style.pointerEvents = "none";

    const panes = this.overlay.getPanes();
    panes.overlayLayer.appendChild(this.canvas);
  }

  onRemove() {
    if (this.canvas && this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }
    this.canvas = null;
  }

  draw() {
    const projection = this.overlay.getProjection();
    const map = this.overlay.getMap();
    if (!this.canvas || !projection || !map || !map.getBounds()) {
      return;
    }

    const bounds = map.getBounds();
    const northEast = projection.fromLatLngToDivPixel(bounds.getNorthEast());
    const southWest = projection.fromLatLngToDivPixel(bounds.getSouthWest());
    const padding = this.radius * 2;
    const left = southWest.x - padding;
    const top = northEast.y - padding;
    const width = northEast.x - southWest.x + padding * 2;
    const height = southWest.y - northEast.y + padding * 2;
    const ratio = window.devicePixelRatio || 1;

    this.canvas.style.left = `${left}px`;
    this.canvas.style.top = `${top}px`;
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    this.canvas.width = Math.ceil(width * ratio);
    this.canvas.height = Math.ceil(height * ratio);

    const context = this.canvas.getContext("2d");
    context.scale(ratio, ratio);
    context.clearRect(0, 0, width, height);

    this.data.forEach((latLng) => {
      const point = projection.fromLatLngToDivPixel(latLng);
      this.drawPoint(context, point.x - left, point.y - top);
    });

    this.applyGradient(context, this.canvas.width, this.canvas.height);
  }

  drawPoint(context, x, y) {
    const gradient = context.createRadialGradient(x, y, 0, x, y, this.radius);
    gradient.addColorStop(0, "rgba(0, 0, 0, 0.45)");
    gradient.addColorStop(1, "rgba(0, 0, 0, 0)");

    context.fillStyle = gradient;
    context.beginPath();
    context.arc(x, y, this.radius, 0, Math.PI * 2);
    context.fill();
  }

  applyGradient(context, width, height) {
    if (!this.colorRamp) {
      this.colorRamp = this.createColorRamp();
    }

    const pixels = context.getImageData(0, 0, width, height);
    const pixelData = pixels.data;

    for (let i = 0; i < pixelData.length; i += 4) {
      const alpha = pixelData[i + 3];
      if (alpha === 0) {
        continue;
      }

      const colorOffset = alpha * 4;
      pixelData[i] = this.colorRamp[colorOffset];
      pixelData[i + 1] = this.colorRamp[colorOffset + 1];
      pixelData[i + 2] = this.colorRamp[colorOffset + 2];
      pixelData[i + 3] = Math.min(255, alpha * 1.4);
    }

    context.putImageData(pixels, 0, 0);
  }

  createColorRamp() {
    const canvas = document.createElement("canvas");
    canvas.width = 256;
    canvas.height = 1;

    const context = canvas.getContext("2d");
    const gradient = context.createLinearGradient(0, 0, 256, 0);
    this.gradient.forEach((color, index) => {
      gradient.addColorStop(index / Math.max(1, this.gradient.length - 1), color);
    });

    context.fillStyle = gradient;
    context.fillRect(0, 0, 256, 1);

    return context.getImageData(0, 0, 256, 1).data;
  }
}

export class HeatMapLayer {
  constructor(map, layer, incidentsLayer) {
    this.heatmapIsAppended = false;
    const gradient = [
      "rgba(0, 255, 255, 0)",
      "rgba(255, 222, 44, 1)",
      "rgba(255, 206, 44, 1)",
      "rgba(255, 190, 44, 1)",
      "rgba(255, 174, 44, 1)",
      "rgba(255, 149, 44, 1)",
      "rgba(244, 121, 31, 1)",
      "rgba(240, 102, 0, 1)",
      "rgba(240, 102, 0, 1)",
      "rgba(229, 80, 0, 1)",
      "rgba(217, 68, 33, 1)",
      "rgba(217, 68, 32, 1)",
      "rgba(217, 32, 32, 1)",
      "rgba(217, 32, 41, 1)",
    ];
    this.heatmap = new CanvasHeatmapOverlay({
      radius: 30,
      gradient,
    });

    this.map = map;

    const toggleHeatmapButton = document.createElement("button");
    toggleHeatmapButton.id = "toggleHeatMapButton";
    toggleHeatmapButton.type = "button";
    toggleHeatmapButton.style.margin = "10px 0 0 10px";
    toggleHeatmapButton.classList = "btn btn-light";
    toggleHeatmapButton.innerHTML = "Toggle Heatmap";
    if (incidentsLayer.isDisabled()) {
      toggleHeatmapButton.disabled = true;
    }

    window.addEventListener("IncidentsLayer_enabled", () => {
      if (incidentsLayer.isVisible()) {
        toggleHeatmapButton.disabled = false;
      }
    });
    window.addEventListener("IncidentsLayer_disabled", () => {
      if (incidentsLayer.isDisabled()) {
        this.heatmapIsAppended = false;
        toggleHeatmapButton.classList.remove("active");
        this.heatmap.setMap(null);
        toggleHeatmapButton.disabled = true;
      }
    });

    toggleHeatmapButton.addEventListener("click", (event) => {
      event.preventDefault();
      if (incidentsLayer.isDisabled() === false) {
        this.load(layer);
        this.heatmap.setMap(this.heatmap.getMap() ? null : this.map);
        toggleHeatmapButton.classList.toggle("active");
        this.heatmapIsAppended = !this.heatmapIsAppended;
        this.heatmapIsAppended ? incidentsLayer.hide() : incidentsLayer.show();
      }
    });

    // eslint-disable-next-line no-undef
    this.map.controls[google.maps.ControlPosition.TOP_LEFT].push(toggleHeatmapButton);
  }

  load(layer) {
    const heatmapData = [];
    layer.forEach((feature) => {
      const latLng = feature.getGeometry().get();
      heatmapData.push(latLng);
    });

    // Needed because old data will still remain in map.
    if (this.heatmap) {
      this.heatmap.setData(heatmapData);
      if (this.heatmapIsAppended) {
        this.heatmap.setMap(this.map);
      }
    }
  }
}
